import React, {
  RefObject,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';
import type {
  PageIntroduction as PageIntroductionContent,
} from 'views/EMR/components/PCC/Walkthrough/types';

type Props = PageIntroductionContent & {
  headingRef?: RefObject<HTMLHeadingElement | null>;
  align?: 'left' | 'center';
  headingSize?: 'overview' | 'content' | 'success';
  className?: string;
};

export default function PageIntroduction({
  eyebrow,
  heading,
  description,
  headingRef,
  align = 'left',
  headingSize = 'content',
  className,
}: Props) {
  const headingClasses = {
    overview: 'text-[32px] font-extrabold leading-[1.15] tracking-[-0.02em] sm:text-[40px] lg:text-[48px]',
    content: 'text-[28px] font-bold leading-[1.2] tracking-[-0.02em] sm:text-[32px]',
    success: 'text-[32px] font-extrabold leading-[1.2] tracking-[-0.01em] sm:text-[38px]',
  }[headingSize];

  return (
    <div
      className={clsx(
        'flex flex-col gap-2.5',
        {
          'items-center text-center': align === 'center',
          'items-start text-left': align === 'left',
        },
        className,
      )}
    >
      {notEmptyString(eyebrow) ? (
        <div
          className={clsx(
            'rounded-full border border-brand-200 bg-brand-100 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.08em] text-brand-700',
            align === 'center' ? 'self-center' : 'self-start',
          )}
        >
          {eyebrow}
        </div>
      ) : null}
      <h1
        ref={headingRef}
        tabIndex={-1}
        className={clsx('m-0 text-[#0F1B2D] outline-none', headingClasses)}
      >
        {heading}
      </h1>
      {!isNil(description) && notEmptyString(description) ? (
        <p
          className={clsx(
            'm-0 max-w-[760px] text-[#475569]',
            headingSize === 'overview'
              ? 'text-[17px] leading-[1.6] sm:text-[19px]'
              : 'text-[17px] leading-normal',
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}
